import { fetchAuthSession } from '@aws-amplify/auth';

import env from '../config/env';
import { BACKEND_SEMANTIC_WORKSPACES_API } from '../env';

const API_BASE = BACKEND_SEMANTIC_WORKSPACES_API;

const SEMANTIC_WORKSPACE_API_KEY =
  env?.API_KEY ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_SEMANTIC_WORKSPACES_API_KEY : undefined) ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_X_API_KEY : undefined);

const defaultHeaders = {
  Accept: 'application/json',
  ...(SEMANTIC_WORKSPACE_API_KEY ? { 'x-api-key': SEMANTIC_WORKSPACE_API_KEY } : {})
};

if (!SEMANTIC_WORKSPACE_API_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    'SemanticWorkspaceService: no se detectó API key (REACT_APP_SEMANTIC_WORKSPACES_API_KEY o REACT_APP_X_API_KEY). ' +
      'Las llamadas al backend podrían responder 401/CORS.'
  );
}

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    const accessToken = tokens?.accessToken?.toString();

    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
    if (accessToken) {
      return { Authorization: `Bearer ${accessToken}` };
    }
  } catch (error) {
    console.error('SemanticWorkspaceService.getAuthHeader: no se pudo obtener la sesión actual', error);
  }

  return {};
}

async function request(url, init = {}) {
  const authHeader = await getAuthHeader();

  const headers = {
    ...defaultHeaders,
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };

  const options = {
    ...init,
    headers,
    mode: 'cors',
    cache: 'no-cache'
  };

  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    throw new Error(`No se pudo contactar el servicio de espacios semánticos (${url}). ${networkError.message}`);
  }

  const requestId =
    response.headers.get('x-amzn-requestid') ||
    response.headers.get('x-amz-apigw-id') ||
    response.headers.get('x-request-id') ||
    'n/a';

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status} (reqId=${requestId}): ${errorBody || 'Error en solicitud'}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function ensureArray(value) {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

function normalizeUsers(users) {
  return ensureArray(users)
    .map((entry) => {
      if (!entry && entry !== 0) {
        return null;
      }

      if (typeof entry === 'string') {
        return { user_id: entry };
      }

      if (typeof entry === 'object') {
        const identifier =
          entry.user_id ||
          entry.userId ||
          entry.USER_ID ||
          entry.id ||
          entry.ID;

        if (!identifier) {
          return null;
        }

        return { user_id: String(identifier) };
      }

      return null;
    })
    .filter(Boolean);
}

function normalizeStringArray(list) {
  return ensureArray(list)
    .map((value) => {
      if (value === undefined || value === null) {
        return null;
      }
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'object' && value.id) {
        return String(value.id);
      }
      return String(value);
    })
    .filter(Boolean);
}

function normalizeSoftDelete(value) {
  if (!value || typeof value !== 'object') {
    return { deleted: false, deleted_at: null };
  }

  const deleted = Boolean(
    value.deleted ??
      value.DELETED ??
      value.is_deleted ??
      value.IS_DELETED ??
      value.soft_deleted
  );
  const deletedAt = value.deleted_at ?? value.DELETED_AT ?? null;

  return {
    deleted,
    deleted_at: deleted ? deletedAt : null
  };
}

function normalizeWorkspace(record) {
  if (!record || typeof record !== 'object') {
    return record ?? null;
  }

  return {
    ...record,
    models_linked: normalizeStringArray(record.models_linked),
    conversations: normalizeStringArray(record.conversations),
    users_allowed: normalizeUsers(record.users_allowed),
    soft_delete: normalizeSoftDelete(record.soft_delete)
  };
}

function buildWorkspacePayload(source = {}) {
  if (!source || typeof source !== 'object') {
    throw new Error('SemanticWorkspaceService: el payload debe ser un objeto');
  }

  const payload = {};

  const mapStringField = (targetKey, ...candidates) => {
    const value = candidates.find(
      (candidate) => candidate !== undefined && candidate !== null && candidate !== ''
    );
    if (value !== undefined && value !== null && value !== '') {
      payload[targetKey] = String(value).trim();
    }
  };

  mapStringField(
    'semantic_workspace_id',
    source.semantic_workspace_id,
    source.semanticWorkspaceId
  );
  mapStringField('account_id', source.account_id, source.accountId);
  mapStringField('user_owner_id', source.user_owner_id, source.userOwnerId, source.owner_id);
  mapStringField('name', source.name, source.displayName, source.workspace_name);
  mapStringField('description', source.description, source.workspace_description);
  // Contexto de negocio libre (ej. "Retail y consumo masivo") que
  // table_metadata_generator usa para sesgar las descripciones/domain que
  // infiere la IA -- ver semantic_workspace/app.py `business_area`.
  mapStringField('business_area', source.business_area, source.businessArea);

  if ('is_active' in source || 'isActive' in source) {
    payload.is_active = Boolean(source.is_active ?? source.isActive);
  }

  if ('soft_delete' in source) {
    payload.soft_delete = {
      deleted: Boolean(source.soft_delete?.deleted),
      deleted_at: source.soft_delete?.deleted_at ?? null
    };
  }

  if ('models_linked' in source) {
    payload.models_linked = normalizeStringArray(source.models_linked);
  }

  if ('conversations' in source) {
    payload.conversations = normalizeStringArray(source.conversations);
  }

  if ('users_allowed' in source) {
    payload.users_allowed = normalizeUsers(source.users_allowed);
  }

  if (source.audit && typeof source.audit === 'object') {
    const audit = {};
    ['created_at', 'created_by', 'updated_at', 'updated_by', 'change_reason'].forEach((key) => {
      const camelKey =
        key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      const value = source.audit[key] ?? source.audit[camelKey] ?? source.audit[key.toUpperCase()];
      if (value !== undefined) {
        audit[key] = value;
      }
    });
    if (Object.keys(audit).length) {
      payload.audit = audit;
    }
  }

  return payload;
}

const SemanticWorkspaceService = {
  async listByAccount(accountId, { includeDeleted = false, limit, nextToken } = {}) {
    if (!accountId) {
      throw new Error('SemanticWorkspaceService.listByAccount: accountId es requerido');
    }

    const params = new URLSearchParams();
    params.set('account_id', accountId);

    if (includeDeleted) {
      params.set('include_deleted', 'true');
    }
    if (limit !== undefined && limit !== null) {
      params.set('limit', String(limit));
    }
    if (nextToken) {
      params.set('next_token', nextToken);
    }

    const query = params.toString();
    const response = await request(`${API_BASE}?${query}`, {
      method: 'GET'
    });

    if (!response) {
      return { items: [], total: 0 };
    }

    if (Array.isArray(response)) {
      return {
        items: response.map(normalizeWorkspace),
        total: response.length
      };
    }

    const items = ensureArray(response.items).map(normalizeWorkspace);
    const total = response.total ?? items.length;

    return { ...response, items, total };
  },

  async listByUser(userId, { includeDeleted = true } = {}) {
    if (!userId) {
      throw new Error('SemanticWorkspaceService.listByUser: userId es requerido');
    }

    const params = new URLSearchParams();
    if (includeDeleted) {
      params.set('include_deleted', 'true');
    }

    const query = params.toString();
    const response = await request(
      query ? `${API_BASE}/user/${encodeURIComponent(userId)}?${query}` : `${API_BASE}/user/${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );

    if (!response) {
      return { items: [], total: 0 };
    }

    if (Array.isArray(response)) {
      return {
        items: response.map(normalizeWorkspace),
        total: response.length
      };
    }

    const items = ensureArray(response.items).map(normalizeWorkspace);
    const total = response.total ?? items.length;

    return { ...response, items, total };
  },

  async create(payload) {
    const body = buildWorkspacePayload(payload);

    const response = await request(API_BASE, {
      method: 'POST',
      body: JSON.stringify(body)
    });

    return normalizeWorkspace(response);
  },

  async update(workspaceId, payload) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.update: workspaceId es requerido');
    }

    const body = buildWorkspacePayload({
      ...payload,
      semantic_workspace_id: payload?.semantic_workspace_id ?? workspaceId
    });

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });

    return normalizeWorkspace(response);
  },

  async softDelete(workspaceId, { deleted = true, change_reason, deleted_at, updated_by, updated_at } = {}) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.softDelete: workspaceId es requerido');
    }

    const body = {
      deleted,
      ...(deleted_at !== undefined ? { deleted_at } : {}),
      ...(change_reason ? { change_reason } : {}),
      ...(updated_by ? { updated_by } : {}),
      ...(updated_at ? { updated_at } : {})
    };

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}/soft-delete`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });

    return normalizeWorkspace(response);
  },

  async restore(workspaceId, options = {}) {
    return this.softDelete(workspaceId, { ...options, deleted: false });
  },

  async remove(workspaceId) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.remove: workspaceId es requerido');
    }

    await request(`${API_BASE}/${encodeURIComponent(workspaceId)}`, {
      method: 'DELETE'
    });

    return { success: true };
  },

  async getUsers(workspaceId) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.getUsers: workspaceId es requerido');
    }

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}/users-allowed`, {
      method: 'GET'
    });

    return normalizeUsers(response);
  },

  async addUsers(workspaceId, users = [], options = {}) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.addUsers: workspaceId es requerido');
    }

    const payloadUsers = normalizeUsers(users);
    if (!payloadUsers.length) {
      throw new Error('SemanticWorkspaceService.addUsers: se requiere al menos un usuario válido');
    }

    const body = {
      users_allowed: payloadUsers,
      ...(options.audit ? { audit: options.audit } : {})
    };

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}/users-allowed`, {
      method: 'POST',
      body: JSON.stringify(body)
    });

    return normalizeUsers(response);
  },

  async removeUser(workspaceId, userId) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.removeUser: workspaceId es requerido');
    }
    if (!userId) {
      throw new Error('SemanticWorkspaceService.removeUser: userId es requerido');
    }

    const response = await request(
      `${API_BASE}/${encodeURIComponent(workspaceId)}/users-allowed/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );

    return normalizeUsers(response);
  },

  async getModels(workspaceId) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.getModels: workspaceId es requerido');
    }

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}/models-linked`, {
      method: 'GET'
    });

    return normalizeStringArray(response);
  },

  async addModels(workspaceId, models = []) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.addModels: workspaceId es requerido');
    }

    const normalized = normalizeStringArray(models);
    if (!normalized.length) {
      throw new Error('SemanticWorkspaceService.addModels: se requiere al menos un modelo válido');
    }

    const body = { models_linked: normalized };

    const response = await request(`${API_BASE}/${encodeURIComponent(workspaceId)}/models-linked`, {
      method: 'POST',
      body: JSON.stringify(body)
    });

    return normalizeStringArray(response);
  },

  async removeModel(workspaceId, modelId) {
    if (!workspaceId) {
      throw new Error('SemanticWorkspaceService.removeModel: workspaceId es requerido');
    }
    if (!modelId) {
      throw new Error('SemanticWorkspaceService.removeModel: modelId es requerido');
    }

    const response = await request(
      `${API_BASE}/${encodeURIComponent(workspaceId)}/models-linked/${encodeURIComponent(modelId)}`,
      { method: 'DELETE' }
    );

    return normalizeStringArray(response);
  },

};

export default SemanticWorkspaceService;
